import { Button, Loading, Tile } from "@carbon/react";
import { Add, Catalog } from "@carbon/icons-react";
import { Toast } from "../../components";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import i18next from "../../i18n";
import RequestHeaderBar from "./NewRequest/RequestHeaderBar";
import RequestDetailsCard from "./NewRequest/RequestDetailsCard";
import ItemsTable from "./NewRequest/ItemsTable";
import NewMaterialModal from "./NewRequest/NewMaterialModal";
import { useNewPurchaseRequest } from "./NewRequest/useNewPurchaseRequest";
import "./NewRequest/newRequest.scss";

export type { ItemRow } from "./NewRequest/types";

/**
 * The New-Purchase-Request page (Carbon + React Hook Form). All state, mutations and handlers live
 * in `useNewPurchaseRequest`; this component is the view wiring only. Redesigned to a header
 * (breadcrumb + title + actions) above two numbered cards: section 1 holds the request details, and
 * section 2 the items table (each row expands into the per-item editor).
 */
const NewPurchaseRequestV2 = () => {
  const vm = useNewPurchaseRequest();

  if (vm.isCreating) {
    return (
      <div className="new-request-loading">
        <Loading withOverlay={false} />
      </div>
    );
  }

  return (
    <div className="new-request">
      <RequestHeaderBar
        onSaveDraft={() => vm.handleSubmit(true)}
        onSend={() => vm.setShowConfirm(true)}
        onCancel={() => vm.navigate(-1)}
        busy={vm.busy}
        canSubmit={vm.canSubmit}
      />

      <div className="new-request__body">
        {/* Section 1 — request details */}
        <Tile className="new-request-section">
          <h2 className="new-request-section__title">
            {`1. ${i18next.t("purchaseRequests.sections.details")}`}
          </h2>
          <RequestDetailsCard
            control={vm.control}
            todayString={vm.todayString}
            singleDateMode={vm.values.singleDateMode}
            onSingleDateModeChange={vm.setSingleDateMode}
            globalNeededFrom={vm.values.globalNeededFrom}
            onGlobalNeededFromChange={vm.handleGlobalNeededFromChange}
            globalNeededTo={vm.values.globalNeededTo}
            onGlobalNeededToChange={vm.handleGlobalNeededToChange}
            globalCurrency={vm.values.globalCurrency}
            onGlobalCurrencyChange={vm.handleGlobalCurrencyChange}
            globalBudgetValue={vm.values.globalBudgetValue}
            onGlobalBudgetValueChange={vm.handleGlobalBudgetValueChange}
            files={vm.values.files}
            isUploadingAttachment={vm.isUploadingAttachment}
            onAttachmentUpload={vm.handleAttachmentUpload}
            onRemoveFile={vm.handleRemoveFile}
            disabled={vm.busy}
          />
        </Tile>

        {/* Section 2 — items */}
        <Tile className="new-request-section">
          <div className="new-request-section__head">
            <h2 className="new-request-section__title">
              {`2. ${i18next.t("purchaseRequests.items.title")} (${vm.items.length})`}
            </h2>
            <div className="new-request-section__actions">
              <Button
                kind="tertiary"
                size="md"
                renderIcon={Catalog}
                onClick={vm.openCreateMaterialModal}
                disabled={vm.busy}
              >
                {i18next.t("purchaseRequests.items.createMaterial")}
              </Button>
              <Button
                size="md"
                renderIcon={Add}
                onClick={vm.handleAddItem}
                disabled={vm.busy}
              >
                {i18next.t("purchaseRequests.items.add")}
              </Button>
            </div>
          </div>

          {vm.items.length === 0 ? (
            <p className="new-request-empty">
              {i18next.t("purchaseRequests.items.addFirst")}
            </p>
          ) : (
            <>
              <ItemsTable
                items={vm.items}
                fields={vm.fields}
                expandedId={vm.expandedId}
                onToggleExpand={vm.handleToggleExpand}
                sites={vm.sites}
                todayString={vm.todayString}
                globalFrom={vm.globalItemDates.neededFrom}
                globalTo={vm.globalItemDates.neededTo}
                globalBudgetValue={
                  vm.values.useGlobalBudget ? vm.values.globalBudgetValue : null
                }
                globalCurrency={vm.values.globalCurrency}
                onItemChange={vm.setItemField}
                onMaterialSelected={vm.handleMaterialSelected}
                onClearMaterial={vm.handleClearMaterial}
                onCreateMaterial={(itemIndex, searchTerm) =>
                  vm.openMaterialModal(
                    {
                      kind: "item",
                      clientId:
                        vm.items[itemIndex]?.clientId ?? `item-${itemIndex}`,
                    },
                    searchTerm
                  )
                }
                onDuplicate={vm.handleDuplicateRow}
                onRemove={vm.removeRow}
                uploadFile={vm.uploadFile}
                onUploadError={vm.openNotification}
              />
              <p className="new-request-table__hint">
                {i18next.t("purchaseRequests.items.inheritedDatesHint")}
              </p>
            </>
          )}
        </Tile>
      </div>

      <ConfirmDialog
        open={vm.showConfirm}
        title={i18next.t("purchaseRequests.modalTitleDeliver")}
        body={i18next.t("purchaseRequests.modalDeliver")}
        confirmLabel={i18next.t("cta.send")}
        onConfirm={() => vm.handleSubmit(false)}
        onCancel={() => vm.setShowConfirm(false)}
      />

      <NewMaterialModal
        open={vm.materialModalOpen}
        onClose={() => vm.setMaterialModalOpen(false)}
        initialName={vm.materialModalName}
        buyerUsers={vm.buyerUsers}
        sites={vm.sites}
        isCreating={vm.isCreatingMaterial}
        isUploadingSpec={vm.isUploadingMaterialSpec}
        specFileURL={vm.materialFileURL}
        onUploadSpec={(file) => vm.uploadMaterialSpec(file)}
        onClearSpec={() => vm.setMaterialFileURL(null)}
        onSubmit={vm.handleCreateMaterialSubmit}
        onValidationError={(message) => vm.openNotification(message)}
      />

      <Toast
        show={vm.showToast}
        onClose={() => vm.setShowToast(false)}
        message={vm.toastMessage}
        type={vm.toastType}
      />
    </div>
  );
};

export default NewPurchaseRequestV2;
